import { isDefined } from "@clipboard-health/util-ts";
import { type Session } from "@src/appV2/Auth/types";
import { type PlacementApplicationWithDetails } from "@src/appV2/PlacementApplications/api/useFetchPaginatedPlacementApplications";
import { InterviewType } from "@src/appV2/PlacementApplications/types/fetchInterviews.schema";
import {
  PlacementApplicationStatus,
  placementApplicationStatusMap,
} from "@src/appV2/PlacementApplications/types/fetchPlacementApplications.schema";
import { InterviewStatus } from "@src/appV2/Placements/types/fetchPlacements.schema";
import { format, parseISO } from "date-fns";
import { startCase } from "lodash";

import { JobType, ShiftNameType } from "../types";
import { jobTypeOptions, shiftTypeOptions } from "./constant";

export function getUserType(profile: Session["profile"], admin: boolean) {
  if (!isDefined(profile)) {
    return "GUEST_EMAIL";
  }

  return admin ? "CBH_EMPLOYEE" : "WORKPLACE_USER";
}

export function getQualificationLabel(qualification: string) {
  if (qualification === "LVN") {
    return "LVN/LPN";
  }

  if (qualification.length <= 3) {
    return qualification;
  }

  return qualification.toLowerCase().split(" ").map(startCase).join(" ");
}

export function getLabel(value: string, type: "jobType" | "shiftType"): string {
  const options = type === "jobType" ? jobTypeOptions : shiftTypeOptions;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  const option = options.find((opt) => opt.value === value);
  return option ? option.label : value;
}

const SHIFT_TYPE_ORDER = [ShiftNameType.AM, ShiftNameType.PM, ShiftNameType.NOC] as const;
type OrderedShiftType = typeof SHIFT_TYPE_ORDER[number];

// Define the order for job types
const JOB_TYPE_ORDER = [
  JobType.FULL_TIME,
  JobType.PART_TIME,
  JobType.PER_DIEM,
  /**
   * @deprecated we no longer support weekend warrior
   */
  JobType.WEEKEND_WARRIOR,
] as const;
type OrderedJobType = typeof JOB_TYPE_ORDER[number];

export function sortAndLabelShiftTypes(
  shiftTypes: string[]
): Array<{ type: OrderedShiftType; label: string }> {
  const validShiftTypes = new Set(
    shiftTypes.filter((type): type is OrderedShiftType =>
      SHIFT_TYPE_ORDER.includes(type as OrderedShiftType)
    )
  );

  return SHIFT_TYPE_ORDER.filter((type) => validShiftTypes.has(type)).map((type) => ({
    type,
    label: getLabel(type, "shiftType"),
  }));
}

export function sortAndLabelJobTypes(
  jobTypes: string[]
): Array<{ type: OrderedJobType; label: string }> {
  const validJobTypes = new Set(
    jobTypes.filter((type): type is OrderedJobType =>
      JOB_TYPE_ORDER.includes(type as OrderedJobType)
    )
  );

  return JOB_TYPE_ORDER.filter((type) => validJobTypes.has(type)).map((type) => ({
    type,
    label: getLabel(type, "jobType"),
  }));
}

export function hasBookedInterview(
  interviews: PlacementApplicationWithDetails["interviews"]
): boolean {
  return interviews.some(
    (interview) =>
      interview.status === InterviewStatus.BOOKED &&
      interview.type === InterviewType.IN_PERSON_INTERVIEW
  );
}

export function getInterviewLabel(placementApplication: {
  readonly status: PlacementApplicationStatus;
  readonly interviews: PlacementApplicationWithDetails["interviews"];
}): string {
  if (placementApplication.status !== PlacementApplicationStatus.INTERVIEW) {
    return placementApplicationStatusMap[placementApplication.status].label;
  }

  const interviewToDisplay = placementApplication.interviews.find(
    (interview) =>
      interview.status === InterviewStatus.BOOKED &&
      interview.type === InterviewType.IN_PERSON_INTERVIEW
  );

  if (!interviewToDisplay?.start) {
    return "Interview";
  }

  const startDateTime = format(parseISO(interviewToDisplay.start), "EEE, MMM d h:mm a");
  const endDateTime = format(parseISO(interviewToDisplay.end), "h:mm a");
  return `Interview: ${startDateTime} - ${endDateTime}`;
}

export function hasPendingInterviewRequests(
  isScheduleInterviewByWorkplace: boolean,
  status: PlacementApplicationStatus,
  bookedInterview: boolean
): boolean {
  return (
    isScheduleInterviewByWorkplace &&
    [PlacementApplicationStatus.APPLIED, PlacementApplicationStatus.ACCEPTED].includes(status) &&
    !bookedInterview
  );
}
